/**
 * A message someone wrote (a call-in reason, a note), shown so the WHOLE thing
 * can always be read on the page — on a phone or tablet too.
 *
 * Short messages show in full. A long one shows its first couple of sentences,
 * cut at a word, and a plain "Show full message" button that opens the rest in
 * place. No hover tooltips and no CSS ellipsis: those hide text on a touch
 * screen with no way to get it back (11/1: a call-in reason cut to "make it i…"
 * in the HR Cockpit table, and missing from Attendance entirely).
 */
import { useState } from "react";

/** Characters shown before the button. About two lines in a table cell. */
export const PREVIEW_CHARS = 140;

/** First PREVIEW_CHARS of the text, cut at the last whole word. Null when it all fits. */
export function previewOf(text: string, limit = PREVIEW_CHARS): string | null {
    if (text.length <= limit) return null;
    const head = text.slice(0, limit);
    const space = head.lastIndexOf(" ");
    return `${(space > limit / 2 ? head.slice(0, space) : head).replace(/[\s,;:.-]+$/, "")}…`;
}

export function FullMessage({ text, className = "" }: { text: string | null | undefined; className?: string }) {
    const [open, setOpen] = useState(false);
    const clean = (text ?? "").trim();
    if (!clean) return null;
    const preview = previewOf(clean);
    return (
        <span className={`whitespace-pre-wrap break-words ${className}`}>
            {open || !preview ? clean : preview}
            {preview && (
                <>
                    {" "}
                    <button
                        type="button"
                        className="font-medium text-sky-700 underline underline-offset-2 dark:text-sky-300"
                        aria-expanded={open}
                        onClick={(e) => {
                            e.stopPropagation();
                            setOpen((v) => !v);
                        }}
                    >
                        {open ? "Show less" : "Show full message"}
                    </button>
                </>
            )}
        </span>
    );
}
